import { VariantErrorType } from 'data/ProductValidation';
import { MAX_PRODUCT_NAME_LENGTH, PRODUCT_TYPE, TAB_KEYS } from './product';
import { AFFILIATE_FORM_LABELS } from './affiliateForm';

export const productDetailsTab = (detailsUrl: string, product: any, isWarehousesEdited: boolean, saveDisabled: boolean, isAIEnabled: any) => {
  // A tab is only reachable once the product exists and is saved; until then it points back
  // at the details page.
  const linkTo = (segment: string) =>
    product && (!product.brand || !product.id || saveDisabled) ? `${detailsUrl}` : `${detailsUrl}/${segment}`;

  return (
    [
      {
        tabKey: TAB_KEYS.DETAILS,
        badge: { content: '', color: '', backgroundColor: '' },
        value: 'Details',
        link: `${detailsUrl}`
      },
      {
        tabKey: TAB_KEYS.WAREHOUSES,
        badge: { content: '', color: '', backgroundColor: '' },
        value: 'Warehouses',
        link: linkTo(TAB_KEYS.WAREHOUSES)
      },
      {
        tabKey: TAB_KEYS.VARIANTS,
        badge: { content: '', color: '', backgroundColor: '' },
        value: 'Variants',
        link: linkTo(TAB_KEYS.VARIANTS)
      },
      // tab will get populated only if AI is enabled, product is loaded, and it is not a subscription.
      isAIEnabled && product && ![PRODUCT_TYPE.SUBSCRIPTION, PRODUCT_TYPE.AFFILIATE].includes(product.productType) && {
        tabKey: TAB_KEYS.SMART_VARIANTS,
        disabled: product?.status === TEXT_LABELS.PUBLISHED || product?.status === TEXT_LABELS.READY,
        badge: { content: 'Beta', color: '#FFFFFF', backgroundColor: '#FC9139' },
        value: 'Smart Variants',
        link: linkTo(TAB_KEYS.SMART_VARIANTS)
      },
      // Affiliate Program products only, rendered to the right of Smart Variants.
      product && product.productType === PRODUCT_TYPE.AFFILIATE && {
        tabKey: TAB_KEYS.AFFILIATE_FORM,
        badge: { content: '', color: '', backgroundColor: '' },
        value: AFFILIATE_FORM_LABELS.TAB,
        link: linkTo(TAB_KEYS.AFFILIATE_FORM)
      }
    ].filter(Boolean)
  )
}

export const TEXT_LABELS = {
  PRODUCT_SUBMITTED_MESSAGE: 'Product submitted successfully.',
  PRODUCT_BUSINESS_UNIT_DEACTIVATED_MESSAGE:
    'This product is in a deactivated Business Unit and was not submitted.',
  PRODUCT_VALIDATION_START_MESSAGE:
    'Validation has been started. This may take a few minutes.',
  VARIANTS: 'variants',
  DETAILS: 'details',
  EDIT_ATTRIBUTES: 'Edit attributes',
  COLUMN_SETTING: 'Column Settings',
  SUBMIT_CONFIRMATION: 'Are you sure you want to submit this product?',
  NEW_PRODUCT: 'newProduct',
  FULFILLED: 'fulfilled',
  PUBLISHED: 'published',
  PRODUCTS: 'products',
  IMPORT_XLSX: 'Import XLSX',
  DRAFT: 'draft',
  PREVIEW: 'Preview',
  C_PREVIEW: 'preview',
  READY: 'ready',
  TOAST_ID: 'submitProduct',
  PRODUCT_SUBMITING: 'The product is being submitted, please wait...',
  SELECT_VENDOR_TO_SUBMIT: 'Please select a vendor before you try to submit a product.',
  SUBMIT_PRODUCT: "Submit product",
  DISCARD_PRODUCT: "Discard",
  CONFRIMATION: "Confirmation Modal",
  KEEP_PRODUCT: "Keep",
  KEEP_OR_DISCARD: 'Do you want to keep warehouse changes?',
  SAVE_PRODUCT: 'saveProduct',
  PRODUCT_SAVING: 'The product is being saved, please wait...',
  DRAFT_SAVED_SUCCESS: 'Draft saved successfully.',
  SELECT_VENDOR_BEFORE_SAVE: 'Please select a vendor before you try to save a product.',
  SAVE: 'Save',
  SUBMIT: 'Submit',
  PRODUCT_MAY_NOT_PUBLISHED: 'This product may not be published until all issues have been resolved',
  CHECK_BOX: 'checkbox',
  ERRORS: 'Errors',
  SKU: 'SKU',
  TYPE: 'Type',
  CLEAR_ALL_FILTERS: 'Clear all filters',
  SURE_TO_DELETE_VARIANTS: 'Are you sure you want to delete selected variants?',
  PRICE_PER_UNIT: 'pricePerUnit',
  SKU_SMALL: 'sku',
  MATERIAL_NUMBER: 'materialNumber',
  HIDDEN: 'hidden',
  MISSING_VALUE: 'MissingValue',
  EMPTY_ATTRIBUTE_VALUE: 'EmptyAttributeValue',
  AVAILABLE: 'available',
  DELIEVERY_LEAD_DAYS: 'deliveryLeadDays',
  POST_ORDER_SHIPPING_COST: 'postOrderShippingCost',
  INVENTORY: 'Inventory',
  WAREHOUSE: 'WAREHOUSE',
  DELETE_VARIANT: '"Delete variant"',
  SEARCH_BY_SKU: 'Search by SKU number',
  PRODUCT_HIDDEN_MESSAGE: 'The product is hidden. If you want to show individual variants, please uncheck the "Hide product" checkbox on the Details page.',
  COMMISSION_MISSING_PRICE_US: 'Commission cannot be calculated: US purchase price is missing or zero. Please set the US purchase price first.',
  COMMISSION_MISSING_PRICE_CA: 'Commission cannot be calculated: CA purchase price is missing or zero. Please set the CA purchase price first.',
  COMMISSION_SECTION_TITLE: 'Commission (optional)',
  COMMISSION_TOOLTIP: 'Configure affiliate commission per region. If left blank, the default Affiliate Program commission is used.',
  COMMISSION_REGION_US: 'United States (US)',
  COMMISSION_REGION_CA: 'Canada (CA)',
  COMMISSION_PERCENTAGE_LABEL: 'Commission %',
  COMMISSION_AMOUNT_LABEL: 'Commission Amount',
  COMMISSION_AVERAGE_HELPER_TEXT: 'Average calculated based on all SKU prices',
  COMMISSION_BANNER_HEADING: 'Commission fields are optional.',
  COMMISSION_BANNER_BULLET_DEFAULT: 'If no commission values are entered, the default Affiliate Program commission will be applied.',
  COMMISSION_BANNER_BULLET_AUTO_CALC: 'If one commission value is entered, the corresponding commission value will be calculated automatically.',
  COMMISSION_BANNER_BULLET_AVERAGE: 'Calculated Commission % and Commission Amount values represent the average across all SKU prices.',
  COMMISSION_BANNER_BULLET_OVERRIDE: 'These values can be overridden at the SKU level.',
  SELECT_TYPE: 'Select type',
  WARNING: 'warning',
  BLACK: 'black',
  No_VARIANTS_AVAILABLE: 'No variants available',
  WAREHOUSES: 'warehouses',
  ADD_WAREHOUSE: 'Add warehouse',
  CREATE_WAREHOUSE: 'Create warehouse',
  PURCHASE_LOG: 'Purchase logistics',
  RENT_LOG: 'Rent logistics',
  SEARCH_WAREHOUSE: 'Search the warehouse',
  NO_WAREHOUSE_SELECTED: 'No warehouse selected ',
  NO_WAREHOUSE_FOUND: 'No warehouse found.',
  PURCHASE_UNIT_PRICE: 'Purchase Price',
  UNIT_PRICES: 'unitPrices',
  RENT_RESTRICTED_STATES: 'Rent restricted states (US)',
  RENT_RESTRICTED_PROVINCE: 'Rent restricted provinces (CA)',
  RANGE: 'range',
  RANGE_PRICE: 'rangePrice',
  RANGE_UNITS: 'rangeUnits',
  RANGE_UNIT: 'rangeUnit',
  RENT: 'rent',
  RENT_C: 'RENT',
  PURCHASE: 'purchase',
  PURCHASE_C: 'PURCHASE',
  AFFILIATE_COMMISSION_US: 'affiliateCommissionUS',
  COMMISSION_AMOUNT_US: 'commissionAmountUS',
  AFFILIATE_COMMISSION_CA: 'affiliateCommissionCA',
  COMMISSION_AMOUNT_CA: 'commissionAmountCA',
  ADD_VIDEOS: 'Add videos',
  PRODUCT_NAME_LENGTH_ERROR: `The product name cannot exceed ${MAX_PRODUCT_NAME_LENGTH} characters. Please shorten the name and try again.`,
  SAVE_PRODUCT_ERROR: 'Please fill all the required fields.',
  RENT_WAREHOUSE_ERROR: 'Please add rental warehouse',
  PURCHASE_WAREHOUSE_ERROR: 'Please add purchase warehouse',
  BOTH_WAREHOUSE_ERROR: 'Please add purchase and rent warehouse',
  LAST_US_RANGE: 'range-5-US',
  LAST_CA_RANGE: 'range-5-CA',
  CONSIGNMENT_TYPE: 'consignmentType',
  RANGE_UNIT_US: 'rangeUnit-US',
  RANGE_UNIT_CA: 'rangeUnit-CA',
  RANGE_UNIT_US_LABEL: 'US unit of measurement',
  RANGE_UNIT_CA_LABEL: 'CA unit of measurement',
  UNITS_OF_MEASUREMENT: 'Units of measurement',
  RENTAL_PRICE_US: 'rentalPrice-US',
  RENTAL_PRICE_CA: 'rentalPrice-CA',
  RENTAL_PRICE_US_LABEL: 'US rental price',
  RENTAL_PRICE_CA_LABEL: 'CA rental price',
  RENTAL_PRICE: 'rentalPrice',
  RENTAL_PRICE_LABEL: 'Rental price',
  MATERIAL_API_VALIDATION_FAILED: "Material number not found. Sales Order will not be created automatically for SKU with invalid material numbers.",
  SMART_VARIANTS: 'smart-variants',
  AFFILIATE_FORM: 'affiliate-form',
  RECENT_PROMPTS: 'Recent prompts',
  REJECT: 'Reject',
  APPROVE: 'Approve',
  REJECT_CONFIRMATION: 'Are you sure you want to reject the entries? All changes will be reset.',
  APPROVE_CONFIRMATION: 'Are you sure you want to approve the entries?',
  CONFIRM_ACTION: 'Confirm Action',
  CANCEL: 'Cancel',
  CONFIRM: 'Confirm',
  CONFIRM_ACTION_DESCRIPTION: 'Are you sure you want to confirm the action?',
  TAB_CHANGE_CONFIRMATION_MESSAGE: 'You have pending variant actions. Are you sure you want to leave this tab?',
  AI_PROCESSING_MESSAGE: 'AI is processing your request...',
  HINT: 'Hint',
  EXAMPLES: 'Examples',
  SMART_VARIANTS_PROMPT_TITLE: `Describe the variants you want, and we’ll generate them.`,
  SMART_VARIANTS_PLACEHOLDER: 'Enter a prompt to generate variants.',
  NO_RECENT_PROMPTS_MESSAGE: 'No recent prompts',
  TOKEN_LIMIT_TITLE: 'Daily Token Limit Reached.',
  RESET_TIME_TITLE: 'Resets at',
  AI_CAN_MAKE_MISTAKES: 'AI can make mistakes. Please review before saving.',
  USER_GUIDE: 'guide',
  SAMPLE_PROMPTS: 'sample-prompts',
  ADD_VARIANT: '+ Add Variant',
}

export const RESTRICTED_TEXT_LABELS: { RESTRICTED_STATES_TEXT: { [key: string]: string } } = {
  RESTRICTED_STATES_TEXT: {
    'US': 'Rent restricted states (US)',
    'CA': 'Rent restricted provinces (CA)'
  }
}

export const errorMessages = {
  [VariantErrorType.DuplicateUniqueValue]: 'Duplicate SKU',
  [VariantErrorType.DuplicateVariant]: 'Duplicate variant',
  [VariantErrorType.MissingValue]: 'Missing or blank values',
  [VariantErrorType.InvalidSKUValue]: 'The value for SKU is invalid. Valid characters are A-Z a-z 0-9 . _ - \\ /. No spaces are allowed',
  [VariantErrorType.InvalidSKULength]: `The SKU should be less than ${process.env.REACT_APP_SKU_MAX_LENGTH!} characters long`,
  [VariantErrorType.SKUValidation]: `The SKU validation has been failed`,
  [VariantErrorType.EmptyAttributeValue]: `Empty Attribute value`,
  [VariantErrorType.InvalidMaterialNumber]: 'The value for SKU is invalid. Valid characters are A-Z a-z 0-9 . _ - \\ /. No spaces are allowed',
  [VariantErrorType.EmptyMaterialNumber]: 'Material number Value can not be empty',
  [VariantErrorType.InvalidMaterialNumberLength]: `The SKU should be less than ${process.env.REACT_APP_SKU_MAX_LENGTH!} characters long`,
  [VariantErrorType.DuplicateMaterialNumber]: 'Duplicate material number',
  [VariantErrorType.MaterialNumberValidation]: 'MaterialNumberValidation',
  [VariantErrorType.EmptyInventory]: 'Inventory not updated',
  [VariantErrorType.EmptySku]: 'Sku value is missing in variant',
  [VariantErrorType.RentMissingPriceUS]: 'Price is mandatory for first record',
  [VariantErrorType.RentMissingPriceCA]: 'Price is mandatory for first record',
  [VariantErrorType.RentMissingRangeUS]: 'Range is mandatory for first record',
  [VariantErrorType.RentMissingRangeCA]: 'Range is mandatory for first record',
  [VariantErrorType.RentRangeSkipped]: 'Price ranges must be populated in order',
  [VariantErrorType.RentRangeOrder]: 'Rental ranges should be in ascending order',
  [VariantErrorType.RequiredRentalRange]: 'Rental variants must have at least one price range set',
  [VariantErrorType.EmptyConsignmentType]: 'Either purchase or rent should be selected'
};

export const ROUTES = {
  PRODUCT_DETAIL: '/products/product-details/',
  PRODUCTS: '/products',
  PUBLISHED: '/products/published',
  READY: '/products/ready',
  NEW_PRODUCT: '/products/new-product',
  SUBSCRIPTIONS: '/subscriptions/products',
  SUBSCRIPTION_DETAIL: '/subscriptions/subscription-details/',
  NEW_SUBSCRIPTION: '/subscriptions/new-subscription',
  AFFILIATE_PROGRAM: '/affiliates/program',
  AFFILIATE_PROGRAM_DETAIL: '/affiliates/program-details/',
  NEW_AFFILIATE_PROGRAM: '/affiliates/new-affiliate-program',
  AFFILIATE_PRODUCTS: '/affiliates/products',
}

export const attributeErrorMessage = (serverMessage: string = ''): string => {
  return (
    'Some attributes were omitted.' +
    (serverMessage ? ` Reason: ${serverMessage}` : '')
  );
};

export const WAREHOUSE_COLUMNS_ENABLED = ['available-', "deliveryLeadDays-", "postOrderShippingCost-"]

export const disabledColumns = [];